import React, { ComponentType, useRef } from 'react';
import { useContextAnalytics } from '../../context/AnalyticsContext';
import useFirebaseAnalytics from './../../hooks/useFirebaseAnalytics';
import { EventParams } from './AnalyticsTypes';

export const createDebounce = (delay: number = 500) => {
  let timeoutId: NodeJS.Timeout;

  return (callback: () => void) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };
};

type Interaction = 'button' | 'selector' | 'textField' | 'checkbox' | 'update';

export const withAnalyticsElement = (Component: ComponentType<any>, interaction: Interaction) => {
  return (props: any) => {
    const { eventname, eventparams, analyticsExtraParams, ...restProps } = props;
    const debounceRef = useRef<ReturnType<typeof createDebounce> | null>(null);

    const isButton = interaction === 'button';
    const isSelector = interaction === 'selector';
    const isTextField = interaction === 'textField';

    const isCheckbox = interaction === 'checkbox';
    const isUpdate = interaction === 'update';

    if (isTextField && !debounceRef.current) {
      debounceRef.current = createDebounce();
    }

    const contextAnalytics = useContextAnalytics();
    const analytics = useFirebaseAnalytics();

    const logAnalytics = (customParams: EventParams = {}) => {
      if (!eventname && !props.id) {
        // if the dev env console.log this
        return;
      }

      const eventParams: EventParams = {
        ...eventparams,
        ...customParams,
      };

      const eventName = eventname || props.id;

      if (contextAnalytics) {
        contextAnalytics.trackEvent(eventName, eventParams);
      } else {
        analytics.logEvent(eventName, eventParams);
      }
    };

    const selectedValue = (value: any): EventParams => {
      const selection = value?.target ? value.target.value : value;
      return {
        selected_value:
          typeof selection?.format === 'function' ? selection.format('YYYY-MM-DD') : selection,
      };
    };

    return (
      <Component
        {...restProps}
        onChange={(e: any) => {
          if (isCheckbox) {
            logAnalytics({ is_checked: e.target.checked });
          } else if (isTextField && debounceRef.current) {
            debounceRef.current(() => logAnalytics());
          } else if (isSelector) {
            logAnalytics({
              ...selectedValue(e),
              ...(analyticsExtraParams?.(e) || {}),
            });
          } else if (!isTextField && !isUpdate && !isButton) {
            logAnalytics();
          }
          restProps?.onChange?.(e);
        }}
        {...(isUpdate && {
          onUpdate: (value: any) => {
            logAnalytics();
            restProps?.onUpdate?.(value);
          },
        })}
        onClick={(e: any) => {
          if (isButton) {
            logAnalytics();
          }
          restProps?.onClick?.(e);
        }}
        onSubmit={(e: any) => {
          logAnalytics();
          restProps?.onSubmit?.(e);
        }}
      />
    );
  };
};
